import { useModal } from "@/app/shared/modal-views/use-modal";
import { Form } from "@/ui/form";
import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { PiXBold } from "react-icons/pi";
import { ActionIcon, Button, Input, Password, Select, Title } from "rizzui";
import { createUser } from "@/server/api-services";
import { Register } from "@/types";

export default function UserForm({
    title,
    refreshUsers,
    register,
}: {
    title?: string;
    refreshUsers?: () => void;
    register?: Register;
}) {
    const { closeModal } = useModal();
    const [reset, setReset] = useState({});

    const formMethods = useForm<Register>({ defaultValues: register || {} });

    const [isLoading, setLoading] = useState(false);

    const onSubmit: SubmitHandler<Register> = async (data) => {
        setLoading(true);
        try {
            const formattedData =
                register != null
                    ? register
                    : {
                          ...data,
                          createdAt: new Date(),
                      };
            await createUser(formattedData);
            setReset({
                fullName: "",
                email: "",
                role: "",
                permissions: "",
                status: "",
            });
            closeModal();
            refreshUsers && refreshUsers(); // Refresh users
        } catch (error) {
            console.error("Failed to create user", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Form<Register>
            resetValues={reset}
            onSubmit={onSubmit}
            {...formMethods}
            className="grid grid-cols-1 gap-6 p-6 @container md:grid-cols-2 [&_.rizzui-input-label]:font-medium [&_.rizzui-input-label]:text-gray-900"
        >
            {({ register, control, formState: { errors } }) => {
                return (
                    <>
                        <div className="col-span-full flex items-center justify-between">
                            <Title as="h4" className="font-semibold">
                                {title || "Add a new User"}
                            </Title>
                            <ActionIcon
                                size="sm"
                                variant="text"
                                onClick={closeModal}
                            >
                                <PiXBold className="h-auto w-5" />
                            </ActionIcon>
                        </div>
                        <Input
                            label="Full Name"
                            placeholder="Enter user's full name"
                            {...register("fullName")}
                            className="col-span-full"
                            error={errors.fullName?.message}
                        />

                        <Input
                            label="Title"
                            placeholder="Enter user's title"
                            {...register("title")}
                            className="col-span-full"
                            error={errors.title?.message}
                        />

                        <Input
                            label="Email"
                            placeholder="Enter user's Email Address"
                            className="col-span-full"
                            {...register("email")}
                            error={errors.email?.message}
                        />
                        <Input
                            label="Mobile"
                            placeholder="Enter user's Mobile Number"
                            className="col-span-full"
                            {...register("mobile")}
                            error={errors.mobile?.message}
                        />

                        <Controller
                            name="countryISOTwo"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    label="Country"
                                    labelClassName="font-medium text-gray-900"
                                    placeholder="Select user's country"
                                    options={[
                                        { value: "KE", label: "Kenya" },
                                        { value: "TZ", label: "Tanzania" },
                                        { value: "UG", label: "Uganda" },
                                        { value: "RW", label: "Rwanda" },
                                        { value: "BI", label: "Burundi" },
                                        { value: "SS", label: "South Sudan" },
                                        { value: "ET", label: "Ethiopia" },
                                        { value: "SO", label: "Somalia" },
                                        { value: "DJ", label: "Djibouti" },
                                        { value: "ER", label: "Eritrea" },
                                    ]}
                                    {...field}
                                    error={errors.countryISOTwo?.message}
                                />
                            )}
                        />

                        <Password
                            label="Password"
                            placeholder="Enter your password"
                            size="lg"
                            className="[&>label>span]:font-medium"
                            inputClassName="text-sm"
                            {...register("password")}
                            error={errors.password?.message}
                        />
                        <Password
                            label="Confirm Password"
                            placeholder="Confirm your password"
                            size="lg"
                            className="[&>label>span]:font-medium"
                            inputClassName="text-sm"
                            {...register("confirmPassword")}
                            error={errors.confirmPassword?.message}
                        />
                        <div className="col-span-full flex items-center justify-end gap-4">
                            <Button
                                variant="outline"
                                onClick={closeModal}
                                className="w-full @xl:w-auto"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                isLoading={isLoading}
                                className="w-full @xl:w-auto"
                            >
                                Create User
                            </Button>
                        </div>
                    </>
                );
            }}
        </Form>
    );
}
